import {IssueRelativeTime} from '@github-ui/ago'
import {MoveDialogTrigger} from '@github-ui/drag-and-drop'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {commitUnpinIssueMutation} from '@github-ui/issue-viewer/commitUnpinIssueMutation'
import {getIssueStateIcon} from '@github-ui/list-view-items-issues-prs/StateIcon'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {CommentIcon, GrabberIcon, KebabHorizontalIcon, PinSlashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Link} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useRef} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {ERRORS} from '../../constants/errors'
import type {PinnedIssueIssue$key} from './__generated__/PinnedIssueIssue.graphql'
import styles from './PinnedIssue.module.css'

type PinnedIssueProps = {
  issue: PinnedIssueIssue$key
  totalPinnedIssues?: number
}

export function PinnedIssue({issue, totalPinnedIssues}: PinnedIssueProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment PinnedIssueIssue on Issue {
        id
        title
        titleHTML
        url
        createdAt
        state
        stateReason
        number
        author {
          login
          url
        }
        totalCommentsCount
        repository {
          id
          viewerCanPinIssues
        }
      }
    `,
    issue,
  )

  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()

  const unpin = useCallback(() => {
    if (!data) return

    commitUnpinIssueMutation({
      environment,
      input: {issueId: data.id},
      repositoryId: data.repository.id,
      currentPinnedCount: totalPinnedIssues ?? 0,
      onCompleted: () => {},
      onError: () => {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotUnpinIssue,
        })
      },
    })
  }, [addToast, data, environment, totalPinnedIssues])

  const editButtonRef = useRef<HTMLButtonElement>(null)
  if (!data) return <></>

  const stateOrReason = data.state === 'CLOSED' && data.stateReason === 'NOT_PLANNED' ? 'NOT_PLANNED' : data.state
  const state = getIssueStateIcon(stateOrReason)
  const commentCount = data.totalCommentsCount ?? 0
  const StateIcon = state.icon

  return (
    <div className={styles.PinnedIssueContainer}>
      <div className={styles.contentContainer}>
        <div className={styles.titleOptionsContainer}>
          <Link aria-label={`View ${data.title}`} className={clsx('css-truncate', styles.Link)} href={data.url} muted>
            <span style={{'--state-icon-color': state.color}} className={styles.Octicon}>
              <StateIcon aria-label={state.description} />
            </span>
            <VerifiedHTMLText
              // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side
              html={data.titleHTML as SafeHTMLString}
            />
          </Link>
          {data.repository.viewerCanPinIssues && (
            <ActionMenu anchorRef={editButtonRef}>
              <ActionMenu.Anchor>
                <IconButton
                  size="small"
                  icon={KebabHorizontalIcon}
                  variant="invisible"
                  aria-label="Pinned issue options"
                  className={styles.IconButton}
                />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="medium">
                <ActionList>
                  {data && (
                    <ActionList.Item onSelect={unpin} aria-label={`Unpin issue #${data.number}, ${data.title}`}>
                      <ActionList.LeadingVisual>
                        <PinSlashIcon />
                      </ActionList.LeadingVisual>
                      Unpin
                    </ActionList.Item>
                  )}
                  <MoveDialogTrigger Component={ActionListItemReorder} returnFocusRef={editButtonRef} />
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
        </div>
        <div className={styles.metadataContainer}>
          <span className={styles.issueMetadata}>
            #{data.number} &middot;{' '}
            {data.author && (
              <Link
                aria-label={`View ${data.author.login} profile`}
                href={data.author.url}
                {...getHovercardAttributesForType('user', {login: data.author.login})}
                muted
              >
                {data.author.login}
              </Link>
            )}{' '}
            {/* Added whitespace:normal to prevent the cards becoming different sizes to accomodate the timestamp length */}
            <span>opened </span>
            <IssueRelativeTime datetime={data.createdAt} />
          </span>
          {commentCount > 0 && (
            <div className={styles.commentCountContainer}>
              <CommentIcon aria-label={`${commentCount} comments`} className={styles.commentIcon} />
              <span className={styles.commentCountText}>{commentCount}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// onClick is set automatically by the MoveDialogTrigger component
function ActionListItemReorder({onClick}: {onClick?: () => void}) {
  return (
    <ActionList.Item onSelect={onClick}>
      <ActionList.LeadingVisual>
        <GrabberIcon />
      </ActionList.LeadingVisual>
      Advanced move...
    </ActionList.Item>
  )
}
